// Convenience hook: merges derived operational alerts with persisted user state.
import { useMemo } from 'react';
import { useAgency } from '@/contexts/AgencyContext';
import { useOperationalAlerts, type OperationalAlert } from './use-operational-alerts';
import { useAlertStates, isStateActive, isUnread, type AlertStateRow } from './use-alert-lifecycle';

export interface MergedAlert extends OperationalAlert {
  state?: AlertStateRow;
  isActive: boolean;
  isUnread: boolean;
  isCritical: boolean;
}

export function useMergedAlerts() {
  const { agencyId } = useAgency();
  const alertsQ = useOperationalAlerts(agencyId);
  const statesQ = useAlertStates();

  const alerts: OperationalAlert[] = alertsQ.data ?? [];
  const states = statesQ.data ?? {};

  return useMemo(() => {
    const merged: MergedAlert[] = alerts.map(a => {
      const state = states[a.id];
      const active = isStateActive(state);
      return {
        ...a,
        state,
        isActive: active,
        isUnread: active && isUnread(state),
        isCritical: a.severity === 'critical',
      };
    });

    // History: resolved/dismissed/auto-cleared state rows that no longer appear
    // in the live derivation list — these are "completed" alerts.
    const liveKeys = new Set(alerts.map(a => a.id));
    const historyFromState: MergedAlert[] = Object.values(states)
      .filter(s => !liveKeys.has(s.alert_key))
      .map(s => ({
        id: s.alert_key,
        kind: (s.alert_kind as any) ?? 'import_failed',
        severity: 'info' as const,
        title: s.alert_key,
        body: 'Auto-resolved — underlying condition no longer present.',
        brandId: s.brand_id,
        brandName: null,
        ts: s.updated_at,
        state: s,
        isActive: false,
        isUnread: false,
        isCritical: false,
      }));

    const active = merged.filter(a => a.isActive);
    const inactive = [
      ...merged.filter(a => !a.isActive),
      ...historyFromState,
    ];

    return {
      isLoading: alertsQ.isLoading || statesQ.isLoading,
      all: merged,
      active,
      inactive,
      unreadCount: active.filter(a => a.isUnread || a.isCritical).length,
    };
  }, [alerts, states, alertsQ.isLoading, statesQ.isLoading]);
}
